import { useCallback, useMemo } from 'react'
import { useGetTreeById, useTreeGetter } from '../reducers/trees_hooks'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  allProductsById,
  allProductsByPrimaryTreeId,
  allProductsWithPrimaryTreeByIdAtom,
  groupsByProductId,
  useMastersByIdWithErrorNotification,
} from './masters.atoms'
import { IMigrationInfo } from '../components/masters_management/utility'
import {
  useCreateLeaf,
  useUpdateLeafData,
} from '../components/tree/TreeRequests'
import { deleteLeaves } from '../components/utility/TreeHelpers'
import { INodeTagRequest } from 'shared/types/tree'
import { doAdminMutation } from 'providers/mutations'

export function useLeafOperations({
  treeId,
  versionId,
  nodeId,
}: {
  treeId: string
  versionId: string
  nodeId: string
}) {
  const doUpdateLeaf = useUpdateLeafData()
  const doCreateLeaf = useCreateLeaf()
  const { resetTree } = useGetTreeById({ treeId, versionId })
  return useMemo(() => {
    const createLeaf = async (type: string, body: Record<string, any>) => {
      const createParams = {
        tree_id: treeId,
        version: versionId,
        id: nodeId,
        leaves: [
          {
            type,
            body,
          },
        ],
      }
      await doCreateLeaf(createParams)
      resetTree?.()
    }

    const updateLeaf = async (leafId: string, body: Record<string, any>) => {
      await doUpdateLeaf({
        tree_id: treeId,
        version: versionId,
        id: leafId,
        body,
      })
      resetTree?.()
    }

    const deleteLeaf = async (leaf: {
      id: string
      tree_id: string
      version: string
    }) => {
      await deleteLeaves(leaf)
      resetTree?.()
    }

    return {
      createLeaf,
      updateLeaf,
      deleteLeaf,
    }
  }, [treeId, versionId, nodeId, resetTree])
}

export function useEditableIdsForPrimaryTreeId(primaryId: string) {
  return useRetrieveEditableForPrimary()(primaryId)
}

export function useRetrieveEditableForPrimary() {
  const products = useAtomValue(allProductsByPrimaryTreeId)
  return useCallback(
    (primaryId: string) => {
      const product = products[primaryId]
      if (!product?.editable_tree) return {}
      return {
        treeId: product.editable_tree.tree_id,
        versionId: product.editable_tree.version_id,
      }
    },
    [products],
  )
}

export function useEditableTreeForProduct(productId: number) {
  return useGetEditableTreeForProduct()(productId)
}

export function useGetEditableTreeForProduct() {
  const allProducts = useAtomValue(allProductsById)
  const getter = useTreeGetter()

  return useCallback(
    (productId: number) => {
      const product = allProducts[productId]
      if (!product?.editable_tree) return { tree: undefined }
      return getter({
        treeId: product.editable_tree.tree_id,
        versionId: product.editable_tree.version_id,
      })
    },
    [allProducts, getter],
  )
}

export function useGetPrimaryTreeForProduct() {
  const products = useAtomValue(allProductsWithPrimaryTreeByIdAtom)
  const getter = useTreeGetter()

  return useCallback(
    (productId: number) => {
      const product = products[productId]
      if (!product?.primary_tree) return { tree: undefined }
      return getter({
        treeId: product.primary_tree.tree_id,
        versionId: product.primary_tree.version_id,
      })
    },
    [products, getter],
  )
}

export function usePrimaryTreeForProduct(productId: number) {
  return useGetPrimaryTreeForProduct()(productId)
}

export function useEditableTreeForPrimaryTreeId(primaryId: string) {
  const editableIds = useEditableIdsForPrimaryTreeId(primaryId)
  return useGetTreeById(editableIds ?? {})
}

export function useGroupForPrimaryTreeId() {
  const groups = useAtomValue(groupsByProductId)
  const products = useAtomValue(allProductsByPrimaryTreeId)
  return useCallback(
    (primaryTreeId: string) => {
      const product = products[primaryTreeId]
      if (!product) return
      return groups[product.id]
    },
    [groups, products],
  )
}

export function useGetGroupForProduct() {
  const groups = useAtomValue(groupsByProductId)
  return useCallback(
    (product: { id: number }) => {
      if (product.id in groups) {
        return groups[product.id]
      }
    },
    [groups],
  )
}

export function useGetProductForId() {
  const products = useAtomValue(allProductsById)
  return useCallback(
    (productId: number) => {
      if (!(productId in products)) return
      return products[productId]
    },
    [products],
  )
}

export function useProductForId(productId: number) {
  return useGetProductForId()(productId)
}

export function useProductWithPrimaryTreeForId(productId: number) {
  const products = useAtomValue(allProductsWithPrimaryTreeByIdAtom)
  if (!(productId in products)) return
  return products[productId]
}

export function useMastersForProduct(productId: number) {
  const mastersById = useMastersByIdWithErrorNotification()
  const product = useProductForId(productId)
  if (!mastersById || !product?.primary_tree_tree_id) return
  return mastersById[product.primary_tree_tree_id]
}

export function useGroupForProduct(productId: number) {
  const getter = useGetGroupForProduct()
  return getter({ id: productId })
}

export function useLatestMasterForTreeId(treeId: string) {
  const mastersById = useMastersByIdWithErrorNotification()
  if (mastersById && treeId in mastersById) {
    return mastersById[treeId][0]
  }
}

export function useMigrationMastersForProduct() {
  const products = useAtomValue(allProductsById)
  const masters = useMastersByIdWithErrorNotification()

  return useCallback(
    (productId: number): IMigrationInfo => {
      const product = products[productId]
      const errors: string[] = []
      if (!product) {
        errors.push(`No Product Found: ${productId}`)
      } else if (!product.primary_tree_tree_id) {
        errors.push('No primary tree for product')
      } else if (!masters) {
        errors.push('Error retrieving masters')
      } else if (!(product.primary_tree_tree_id in masters)) {
        errors.push('No master versions for product')
      }

      const [toVersion, fromVersion] =
        masters?.[product?.primary_tree_tree_id ?? 'missing'] ?? []
      return {
        master: {
          masterId: product.primary_tree_tree_id!,
          title: product.title,
        },
        toVersion,
        fromVersion,
        errors,
        info: [],
        mismatchedTypeMapping: [],
        oldVersionsWithSchools: [],
        dupMappings: {},
      }
    },
    [masters, products],
  )
}

export function useNodeTagOperations() {
  return useMemo(() => {
    const addNodeTags = async (req: INodeTagRequest) => {
      const res = await doAdminMutation(
        { key: 'addNodeTags' },
        { node_tag: req },
      )
      return res
    }

    return {
      addNodeTags,
    }
  }, [])
}
